/**
 * Zichtbare checklist API-/productcontract (23 punten) — inhoud gelijk aan docs/CONTRACT_23_PUNTEN_STATUS.md.
 */
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

type RowStatus = 'af' | 'deels' | 'roadmap';

const ROWS: Array<{ n: number; label: string; status: RowStatus }> = [
  { n: 1, label: '/api/v2.3 + camelCase succes-JSON; legacy blijft snake_case', status: 'deels' },
  { n: 2, label: 'Uniform foutmodel { error: { code, message, details } } op JSON-fouten', status: 'af' },
  { n: 3, label: 'X-RateLimit-*, X-Plan, X-Library-Version (+ tenantcontext waar mogelijk)', status: 'deels' },
  { n: 4, label: 'CORS: If-Match, If-None-Match, Idempotency-Key', status: 'af' },
  { n: 5, label: 'ETag + 304 op GET (o.a. dossier, lijst, /catalog/meta, /me/plan, subresources)', status: 'deels' },
  { n: 6, label: 'revision_seq + If-Match op dossier-PUT; 412 bij conflict', status: 'af' },
  { n: 7, label: 'Atomische bulk-BOQ als één transactie (gedekt door dossier-PUT)', status: 'deels' },
  { n: 8, label: 'library_version vast op dossier + lifecycle/meta in /catalog/meta', status: 'deels' },
  { n: 9, label: 'Domein-API (structuur, tekeningen, metingen, scenario’s, audit, …)', status: 'deels' },
  { n: 10, label: 'POST …/exports + GET jobstatus (MVP stub → /export_project)', status: 'deels' },
  { n: 11, label: 'GET /me/plan met usage/limits uit tenant billing', status: 'deels' },
  { n: 12, label: 'Library/context governance (seed+D1, meta op /catalog/meta)', status: 'deels' },
  { n: 13, label: 'Content-spec QA per element (P10/P90, placeholders) als CI-gates', status: 'roadmap' },
  { n: 14, label: 'norm_reference / provenance / variance op alle elementen', status: 'roadmap' },
  { n: 15, label: 'Geen listing zonder projectcontext (+ zoek max. 20 systemen)', status: 'af' },
  { n: 16, label: 'Periodieke validatie/backtesting als release-gate', status: 'roadmap' },
  { n: 17, label: 'UI aligned met API (If-Match client, conflictmeldingen)', status: 'deels' },
  { n: 18, label: 'Library-UX (zoek max 20, lifecycle in meta; norm_model niet in UI)', status: 'deels' },
  { n: 19, label: 'Export/rapport: watermerk trial, metadata/juridische laag verder uitbreiden', status: 'deels' },
  { n: 20, label: 'Design system + WCAG AA + bundle-budget (iteratief)', status: 'roadmap' },
  { n: 21, label: 'Security UX (2FA, devices) + disaster-recovery banners', status: 'roadmap' },
  { n: 22, label: 'API-contracttests + E2E dossier→rapport', status: 'roadmap' },
  { n: 23, label: 'Telemetry privacy-first + integraties/webhooks (roadmap)', status: 'roadmap' },
];

function badge(s: RowStatus): { text: string; className: string } {
  switch (s) {
    case 'af':
      return { text: 'Af', className: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100' };
    case 'deels':
      return { text: 'Deels', className: 'bg-amber-100 text-amber-950 dark:bg-amber-950/40 dark:text-amber-50' };
    default:
      return { text: 'Roadmap', className: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200' };
  }
}

export function HelpContractStatus() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <SeoMeta
        path="/admin/contract-status"
        noindex
        title="Contractstatus API — CalqTool"
        description="Intern overzicht: checklist contractpunten en implementatiegraad API t.o.v. specificatie — alleen platformbeheer."
      />
      <div className="mb-3">
        <Breadcrumbs items={[{ label: 'Platformbeheer', href: '/admin' }, { label: 'Contractstatus' }]} />
      </div>
      <h1 className="mb-2 text-2xl font-semibold text-text">Contractstatus (23 punten)</h1>
      <p className="mb-6 text-sm leading-relaxed text-text-muted">
        Technische en productrichtlijnen voor API v2.3, fouten, caching, billing en library. <strong className="text-text">Af</strong>{' '}
        = in deze release uitgevoerd. <strong className="text-text">Deels</strong> = basis staat, uitbreiding mogelijk.{' '}
        <strong className="text-text">Roadmap</strong> = bewust nog niet gebouwd. Zie ook{' '}
        <code className="rounded bg-surface px-1 text-xs">docs/CONTRACT_23_PUNTEN_STATUS.md</code> in de repository.
      </p>
      <ul className="space-y-3">
        {ROWS.map((r) => {
          const b = badge(r.status);
          return (
            <li
              key={r.n}
              className="flex flex-col gap-1 rounded-xl border border-border/80 bg-surface/90 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex gap-3">
                <span className="w-8 shrink-0 font-mono text-sm font-semibold text-text-muted">{r.n}.</span>
                <span className="text-sm text-text">{r.label}</span>
              </div>
              <span className={`shrink-0 self-start rounded-full px-2.5 py-0.5 text-xs font-medium sm:self-center ${b.className}`}>
                {b.text}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-8 text-xs text-text-muted">
        Externe clients kunnen <code className="rounded bg-surface px-1">/api/v2.3/…</code> gebruiken voor camelCase-responses; de webapp
        blijft op <code className="rounded bg-surface px-1">/api/…</code> (legacy snake_case).
      </p>
    </div>
  );
}
